import { ActionList, ConnectionItem, PopoverMenu } from "@wayflyer/flyui";
import { IconMoreVert16Line, IconRefreshDouble16Line, IconXmark16Line } from "@wayflyer/flyui-icons/16/line";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { MESSAGES } from "../message-definitions";
import type { StatementCardState } from "../utils";

// ConnectionItem has no slot for a trailing popover, so this anchors the popover to the card's
// corner while the visible kebab (via ConnectionItem's own `actions`) just toggles it open.
const ANCHOR_STYLE = { position: "absolute", top: 0, right: 0, width: 1, height: 1 } as const;

type StatementRowProps = {
  className?: string;
  label: string;
  logo: ReactNode;
  onReassign?: () => void;
  onRemove: () => void;
  isRemoveDisabled?: boolean;
  cardState?: StatementCardState | "failed";
  onRetry?: () => void;
};

export const StatementRow = ({
  className,
  label,
  logo,
  onReassign,
  onRemove,
  isRemoveDisabled,
  cardState = "normal",
  onRetry,
}: StatementRowProps) => {
  const intl = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The popover's own trigger can't be the real kebab (see ANCHOR_STYLE below), so on close we
  // restore focus to it ourselves rather than let the popover focus its invisible anchor.
  const handleOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      wrapperRef.current?.querySelector("button")?.focus();
    }
  };

  // ConnectionItem's action button isn't the popover's real trigger (see above), so react-aria
  // never applies its menu-relationship attributes there - mirror them onto it by hand so
  // screen readers still get the right semantics.
  useEffect(() => {
    const button = wrapperRef.current?.querySelector("button");
    button?.setAttribute("aria-haspopup", "menu");
    button?.setAttribute("aria-expanded", String(isMenuOpen));
  }, [isMenuOpen, cardState]);

  if (cardState !== "normal") {
    return (
      <ConnectionItem
        className={className}
        label={label}
        logo={logo}
        variant="critical"
        subtitle={intl.formatMessage(
          cardState === "refused" ? MESSAGES.notABankStatementMessage : MESSAGES.uploadFailedMessage
        )}
        actions={[
          ...(cardState === "failed" && onRetry
            ? [
                {
                  id: "retry",
                  icon: IconRefreshDouble16Line,
                  accessibilityLabel: intl.formatMessage(MESSAGES.retryAction),
                  onAction: onRetry,
                },
              ]
            : []),
          {
            id: "remove",
            icon: IconXmark16Line,
            accessibilityLabel: intl.formatMessage(MESSAGES.removeMenuAction),
            disabled: isRemoveDisabled,
            onAction: onRemove,
          },
        ]}
      />
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <ConnectionItem
        className={className}
        label={label}
        logo={logo}
        actions={[
          {
            id: "more",
            icon: IconMoreVert16Line,
            accessibilityLabel: intl.formatMessage(MESSAGES.moreOptionsAccessibilityLabel),
            onAction: () => setIsMenuOpen(true),
          },
        ]}
      />
      <PopoverMenu
        isOpen={isMenuOpen}
        onOpenChange={handleOpenChange}
        placement="bottom-end"
        trigger={<span style={ANCHOR_STYLE} tabIndex={-1} aria-hidden="true" />}
      >
        <ActionList
          items={[
            ...(onReassign
              ? [
                  {
                    id: "reassign",
                    content: intl.formatMessage(MESSAGES.reassignMenuAction),
                    onAction: () => {
                      setIsMenuOpen(false);
                      onReassign();
                    },
                  },
                ]
              : []),
            {
              id: "remove",
              content: intl.formatMessage(MESSAGES.removeMenuAction),
              critical: true,
              disabled: isRemoveDisabled,
              onAction: () => {
                setIsMenuOpen(false);
                onRemove();
              },
            },
          ]}
        />
      </PopoverMenu>
    </div>
  );
};
